import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Calendar,
  Download,
  Eye,
  ArrowRight,
  BarChart3,
  Loader2,
  ChevronRight,
  Send,
  CheckCircle2,
  Trash2,
  Plus
} from 'lucide-react';
import clientReportsAPI from '../api/clientReports';
import { campaignsAPI } from '../api/campaigns';
import { reportsAPI } from '../api/reports';

const Reports = () => {
  const navigate = useNavigate();
  const [allReports, setAllReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null); // null = all, 'draft', 'published', 'sent'

  const { data: campaigns, isLoading: campaignsLoading } = useQuery({
    queryKey: ['campaigns-for-reports'],
    queryFn: async () => {
      try {
        const response = await campaignsAPI.getAll();
        return response.data?.campaigns || response.data || [];
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        return [];
      }
    }
  });

  useEffect(() => {
    const fetchAllReports = async () => {
      if (!campaigns || campaigns.length === 0) {
        setAllReports([]);
        setIsLoadingReports(false);
        return;
      }

      setIsLoadingReports(true);
      const reports = [];

      try {
        for (const campaign of campaigns) {
          try {
            const response = await clientReportsAPI.getCampaignReports(campaign.id);
            const campaignReports = Array.isArray(response.data) ? response.data : (response.data?.reports || []);
            reports.push(...campaignReports.map(r => ({ ...r, campaignId: campaign.id, campaignName: campaign.name, gameImage: campaign.Game?.coverImageUrl })));
          } catch (error) {
            console.error(`Error fetching reports for campaign ${campaign.id}:`, error);
          }
        }

        reports.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        setAllReports(reports);
      } finally {
        setIsLoadingReports(false);
      }
    };

    fetchAllReports();
  }, [campaigns]);

  // Auto-select first campaign with reports
  useEffect(() => {
    if (!selectedCampaignId && allReports.length > 0) {
      setSelectedCampaignId(allReports[0].campaignId);
    }
  }, [allReports, selectedCampaignId]);

  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      await clientReportsAPI.deleteReport(reportId);
      setAllReports(prev => prev.filter(r => r.id !== reportId));
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const handleToggleSent = async (reportId, e) => {
    e.stopPropagation();
    try {
      const { data: updated } = await clientReportsAPI.toggleSent(reportId);
      setAllReports(prev => prev.map(r => r.id === reportId ? { ...r, sentAt: updated.sentAt } : r));
    } catch (error) {
      console.error('Error toggling sent status:', error);
    }
  };

  const handleViewReport = (reportId, campaignId) => {
    navigate(`/client-report/${campaignId}/${reportId}`);
  };

  const handleExportPDF = async (reportId, e) => {
    e.stopPropagation();
    try {
      setDownloadingId(reportId);
      const { data } = await clientReportsAPI.getFullReportData(reportId);
      const response = await reportsAPI.generatePDF(data);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // "<report title> - <game>" so multiple reports for the same campaign get
      // distinct, readable filenames; fall back to campaign name + date.
      const clean = (s) => (s || '').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
      const rawGame = data.campaign?.game;
      const gameName = clean(typeof rawGame === 'object' ? (rawGame?.title || rawGame?.name) : rawGame);
      const titlePart = clean(data.report?.title);
      if (titlePart) {
        link.download = `${gameName ? `${titlePart} - ${gameName}` : titlePart}.pdf`;
      } else {
        const campaignName = clean(data.campaign?.name) || 'report';
        const dateStr = new Date().toISOString().split('T')[0];
        link.download = `${campaignName} - ${dateStr}.pdf`;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'draft': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default: return 'bg-white/10 text-white border-white';
    }
  };

  const isLoading = campaignsLoading || isLoadingReports;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
        <div className="flex flex-col items-center">
          <Loader2 size={32} className="animate-spin text-sc-pink" />
          <span className="text-white mt-4 font-medium">Loading reports...</span>
        </div>
      </div>
    );
  }

  const accentColors = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#06b6d4'];

  const campaignGroups = (campaigns || []).map((campaign, i) => {
    const campaignReports = allReports.filter(r => r.campaignId === campaign.id);
    return {
      campaignId: campaign.id,
      campaignName: campaign.name,
      gameImage: campaign.Game?.coverImageUrl || null,
      reports: campaignReports,
      accent: accentColors[i % accentColors.length],
    };
  });

  // Apply status filter
  const filterReport = (r) => {
    if (!statusFilter) return true;
    if (statusFilter === 'sent') return !!r.sentAt;
    return r.status === statusFilter;
  };

  const filteredCampaignGroups = statusFilter
    ? campaignGroups.map(g => ({ ...g, reports: g.reports.filter(filterReport) })).filter(g => g.reports.length > 0)
    : campaignGroups;

  const selectedGroup = filteredCampaignGroups.find(g => g.campaignId === selectedCampaignId)
    || filteredCampaignGroups[0] || null;
  const selectedReports = selectedGroup?.reports || [];
  const selectedAccent = selectedGroup?.accent || '#ec4899';

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 flex flex-col">
        <div className="p-5 pb-4">
          <h1 className="text-xl font-bold text-white mb-2">Reports</h1>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setStatusFilter(null)}
              className={`text-[10px] px-2 py-0.5 rounded-md transition-all ${
                statusFilter === null
                  ? 'bg-white/15 text-white border border-white'
                  : 'bg-white/5 text-white border border-white hover:bg-white/10'
              }`}
            >
              {allReports.length} total
            </button>
            {allReports.filter(r => r.status === 'draft').length > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'draft' ? null : 'draft')}
                className={`text-[10px] px-2 py-0.5 rounded-md transition-all ${
                  statusFilter === 'draft'
                    ? 'bg-amber-500/25 text-amber-400 border border-amber-500/40'
                    : 'bg-amber-500/10 text-amber-400/70 border border-amber-500/15 hover:bg-amber-500/20'
                }`}
              >
                {allReports.filter(r => r.status === 'draft').length} drafts
              </button>
            )}
            {allReports.filter(r => r.status === 'published').length > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'published' ? null : 'published')}
                className={`text-[10px] px-2 py-0.5 rounded-md transition-all ${
                  statusFilter === 'published'
                    ? 'bg-green-500/25 text-green-400 border border-green-500/40'
                    : 'bg-green-500/10 text-green-400/70 border border-green-500/15 hover:bg-green-500/20'
                }`}
              >
                {allReports.filter(r => r.status === 'published').length} published
              </button>
            )}
            {allReports.filter(r => r.sentAt).length > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'sent' ? null : 'sent')}
                className={`text-[10px] px-2 py-0.5 rounded-md transition-all ${
                  statusFilter === 'sent'
                    ? 'bg-cyan-500/25 text-cyan-400 border border-cyan-500/40'
                    : 'bg-cyan-500/10 text-cyan-400/70 border border-cyan-500/15 hover:bg-cyan-500/20'
                }`}
              >
                {allReports.filter(r => r.sentAt).length} sent
              </button>
            )}
          </div>
        </div>

        {filteredCampaignGroups.length === 0 && allReports.length > 0 ? (
          <div className="px-5 py-8 text-center">
            <FileText className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-xs text-white">No matching reports</p>
            <button onClick={() => setStatusFilter(null)} className="text-[10px] text-sc-pink mt-2 hover:underline">Clear filter</button>
          </div>
        ) : allReports.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <FileText className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-xs text-white">No reports yet</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
            {filteredCampaignGroups.map(({ campaignId, campaignName, reports, accent, gameImage }) => {
              const isSelected = campaignId === selectedCampaignId;
              const draftCount = reports.filter(r => r.status === 'draft').length;
              const pubCount = reports.filter(r => r.status === 'published').length;
              const sentCount = reports.filter(r => r.sentAt).length;

              return (
                <button
                  key={campaignId}
                  onClick={() => setSelectedCampaignId(campaignId)}
                  className={`w-full text-left px-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                    isSelected
                      ? 'bg-white/[0.07] border border-white'
                      : 'hover:bg-white/[0.03] border border-transparent'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-15 blur-2xl" style={{ background: accent }} />
                  )}
                  <div className="relative z-10">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      {gameImage ? (
                        <img src={gameImage} alt="" className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-md flex-shrink-0 bg-white/10 flex items-center justify-center">
                          <FileText className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      <span className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-white group-hover:text-white'}`}>
                        {campaignName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-[38px]">
                      <span className="text-[10px] text-white">{reports.length} report{reports.length !== 1 ? 's' : ''}</span>
                      {pubCount > 0 && <span className="text-[10px] text-green-400/60">{pubCount} published</span>}
                      {draftCount > 0 && <span className="text-[10px] text-amber-400/60">{draftCount} draft{draftCount !== 1 ? 's' : ''}</span>}
                      {sentCount > 0 && <span className="text-[10px] text-cyan-400/60">{sentCount} sent</span>}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <ChevronRight size={14} className="text-white/20" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {selectedGroup ? (
          <>
            {/* Campaign header */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedGroup.gameImage ? (
                    <img src={selectedGroup.gameImage} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="p-2.5 rounded-xl relative overflow-hidden" style={{ background: `${selectedAccent}15` }}>
                      <div className="absolute inset-0 opacity-20 blur-sm" style={{ background: selectedAccent }} />
                      <BarChart3 size={20} style={{ color: selectedAccent }} className="relative z-10" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-white">{selectedGroup.campaignName}</h2>
                    <p className="text-xs text-white">{selectedReports.length} report{selectedReports.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                {/* Status summary */}
                <div className="flex items-center gap-2">
                  {selectedReports.filter(r => r.status === 'published').length > 0 && (
                    <span className="text-[11px] bg-green-500/15 text-green-400 border border-green-500/20 px-2.5 py-1 rounded-lg">
                      {selectedReports.filter(r => r.status === 'published').length} Published
                    </span>
                  )}
                  {selectedReports.filter(r => r.status === 'draft').length > 0 && (
                    <span className="text-[11px] bg-amber-500/15 text-amber-400 border border-amber-500/20 px-2.5 py-1 rounded-lg">
                      {selectedReports.filter(r => r.status === 'draft').length} Draft
                    </span>
                  )}
                  {selectedReports.filter(r => r.sentAt).length > 0 && (
                    <span className="text-[11px] bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 px-2.5 py-1 rounded-lg">
                      <Send className="w-3 h-3 inline mr-1" />
                      {selectedReports.filter(r => r.sentAt).length} Sent
                    </span>
                  )}
                  <button
                    onClick={() => navigate(`/client-report/${selectedGroup.campaignId}/new`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-xs font-semibold ml-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    New Report
                  </button>
                </div>
              </div>
            </div>

            {/* Report list */}
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              {selectedReports.length === 0 ? (
                <div className="flex-1 flex items-center justify-center h-full">
                  <div className="text-center">
                    <FileText className="w-14 h-14 text-white/10 mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-white mb-1.5">No reports for this campaign</h3>
                    <p className="text-white mb-5 text-sm">Create your first report to get started.</p>
                    <button
                      onClick={() => navigate(`/client-report/${selectedGroup.campaignId}/new`)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
                    >
                      <Plus className="w-4 h-4" />
                      Create Report
                    </button>
                  </div>
                </div>
              ) : (
              <div className="space-y-3">
                {selectedReports.map((report, idx) => {
                  const isLatest = idx === 0;

                  return (
                    <div
                      key={report.id}
                      onClick={() => handleViewReport(report.id, report.campaignId)}
                      className={`bg-gray-900/80 border rounded-xl p-5 cursor-pointer group transition-all duration-200 relative overflow-hidden ${
                        isLatest ? 'border-white hover:border-sc-pink/40' : 'border-white hover:border-white'
                      }`}
                    >
                      {isLatest && (
                        <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-10 blur-2xl" style={{ background: selectedAccent }} />
                      )}

                      <div className="relative z-10">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3.5 flex-1 min-w-0">
                            {selectedGroup.gameImage ? (
                              <img src={selectedGroup.gameImage} alt="" className={`w-10 h-10 rounded-lg flex-shrink-0 mt-0.5 object-cover ${isLatest ? 'ring-1 ring-sc-pink/30' : ''}`} />
                            ) : (
                              <div className={`p-2.5 rounded-lg flex-shrink-0 mt-0.5 transition-colors ${isLatest ? 'bg-sc-pink/10 border border-sc-pink/20' : 'bg-white/5 group-hover:bg-white/8'}`}>
                                <FileText className={`w-5 h-5 transition-colors ${isLatest ? 'text-sc-pink' : 'text-white group-hover:text-white'}`} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2.5 mb-1">
                                <h3 className={`font-semibold truncate group-hover:text-sc-pink transition-colors ${isLatest ? 'text-white text-base' : 'text-white text-sm'}`}>
                                  {report.title
                                    || (report.reportPeriodStart && report.reportPeriodEnd
                                      ? `Report: ${formatDate(report.reportPeriodStart)} - ${formatDate(report.reportPeriodEnd)}`
                                      : 'Untitled Report')}
                                </h3>
                                {report.sentAt ? (
                                  <button
                                    onClick={(e) => handleToggleSent(report.id, e)}
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-semibold rounded-full border flex-shrink-0 transition-all bg-cyan-500/20 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/30"
                                    title={`Sent ${formatDate(report.sentAt)} — click to undo`}
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                    Sent
                                  </button>
                                ) : (
                                  <>
                                    <span className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-full border flex-shrink-0 ${getStatusColor(report.status)}`}>
                                      {report.status?.charAt(0).toUpperCase() + report.status?.slice(1)}
                                    </span>
                                    <button
                                      onClick={(e) => handleToggleSent(report.id, e)}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border flex-shrink-0 transition-all bg-white/5 text-white border-white hover:bg-white/10 hover:text-white"
                                      title="Mark as sent"
                                    >
                                      <Send className="w-3 h-3" />
                                      Mark sent
                                    </button>
                                  </>
                                )}
                                {isLatest && (
                                  <span className="text-[10px] text-sc-pink/60 bg-sc-pink/10 px-2 py-0.5 rounded-full border border-sc-pink/15 flex-shrink-0">Latest</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-white">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>Updated {formatDate(report.updatedAt)}</span>
                                {report.reportPeriodStart && report.reportPeriodEnd && (
                                  <>
                                    <span className="text-white/15 mx-1">|</span>
                                    <span>Period: {formatDate(report.reportPeriodStart)} - {formatDate(report.reportPeriodEnd)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => handleExportPDF(report.id, e)}
                              disabled={downloadingId === report.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white text-white hover:text-white hover:bg-white/10 transition-all text-xs font-medium"
                            >
                              {downloadingId === report.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Download className="w-3.5 h-3.5" />
                              )}
                              PDF
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewReport(report.id, report.campaignId); }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/10 border border-sc-pink/20 text-sc-pink hover:bg-sc-pink/20 transition-all text-xs font-medium"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                            <button
                              onClick={(e) => handleDeleteReport(report.id, e)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400/70 hover:text-red-400 hover:bg-red-500/20 transition-all text-xs font-medium"
                              title="Delete report"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No reports yet</h3>
              <p className="text-white mb-6 text-sm">Reports will appear here once you create them from your campaigns.</p>
              <button onClick={() => navigate('/dashboard/campaigns')} className="btn-pink inline-flex items-center">
                Go to Campaigns
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;